import { CardPreviewSurface, CardPreviewSurfaceCrop } from '../card-preview.types';

const SPREAD_ASPECT_RATIO_TOLERANCE = 0.05;

/**
 * Maps inside surface types to the side of a full-spread image they should show.
 *
 * @param type Normalized preview surface type
 * @returns Crop side for split inside panels, or undefined for other surfaces
 */
const getInsideCropSide = (type: string): CardPreviewSurfaceCrop | undefined => {
  if (type === 'inside-left') return 'left';
  if (type === 'inside-right') return 'right';
  return undefined;
};

/**
 * Detects when an inside preview surface is backed by a full-spread image.
 *
 * Some POD inside panels are exported as one wide spread and need CSS cropping
 * to show only the left or right side. Already split portrait snapshots return
 * undefined so they render as-is.
 *
 * @param type Normalized preview surface type
 * @param sourceDimensions Dimensions of the snapshot/background image
 * @param faceDimensions Original face dimensions from card state/template data
 * @returns Crop side when a full-spread source should be split
 */
export const getPreviewSurfaceCrop = (
  type: string,
  sourceDimensions: CardPreviewSurface['dimensions'],
  faceDimensions: CardPreviewSurface['dimensions'],
): CardPreviewSurfaceCrop | undefined => {
  const cropSide = getInsideCropSide(type);
  if (!cropSide || sourceDimensions.height === 0 || faceDimensions.height === 0) return undefined;

  const sourceAspectRatio = sourceDimensions.width / sourceDimensions.height;
  const faceAspectRatio = faceDimensions.width / faceDimensions.height;
  const matchesFaceSpread =
    Math.abs(sourceAspectRatio - faceAspectRatio) / faceAspectRatio < SPREAD_ASPECT_RATIO_TOLERANCE;

  return sourceAspectRatio > 1 && matchesFaceSpread ? cropSide : undefined;
};
